import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Bell, 
  Calendar, 
  MessageSquare, 
  AlertCircle, 
  CheckCircle,
  Clock,
  Trash2,
  CheckCheck
} from 'lucide-react';
import { notificationService } from '@/services/api';
import { format, formatDistanceToNow } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

// Mock notifications for demo
const getMockNotifications = (lang) => [
  {
    id: 1,
    type: 'appointment',
    title: lang === 'ar' ? 'تذكير بالموعد' : 'Appointment Reminder',
    message: lang === 'ar' 
      ? 'موعدك مع د. سارة جونسون غداً الساعة 10:00 صباحاً'
      : 'Your appointment with Dr. Sarah Johnson is tomorrow at 10:00 AM',
    time: new Date(Date.now() - 1000 * 60 * 30), // 30 mins ago
    read: false,
  },
  {
    id: 2,
    type: 'message',
    title: lang === 'ar' ? 'رسالة جديدة' : 'New Message',
    message: lang === 'ar' 
      ? 'لديك رسالة جديدة من د. أحمد حسن'
      : 'You have a new message from Dr. Ahmed Hassan',
    time: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
    read: false,
  },
  {
    id: 3,
    type: 'success',
    title: lang === 'ar' ? 'تم تأكيد الحجز' : 'Booking Confirmed',
    message: lang === 'ar' 
      ? 'تم تأكيد حجزك مع د. فاطمة الرشيد يوم الأحد'
      : 'Your booking with Dr. Fatima Al-Rashid on Sunday is confirmed',
    time: new Date(Date.now() - 1000 * 60 * 60 * 24), // 1 day ago
    read: true,
  },
  {
    id: 4,
    type: 'alert',
    title: lang === 'ar' ? 'تحديث مهم' : 'Important Update',
    message: lang === 'ar' 
      ? 'تم تغيير موقع عيادة د. مايكل براون'
      : 'Dr. Michael Brown\'s clinic location has changed',
    time: new Date(Date.now() - 1000 * 60 * 60 * 48), // 2 days ago
    read: true,
  },
];

const NotificationsPage = () => {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        if (isAuthenticated) {
          const response = await notificationService.getAll();
          const rawNotifications = response.data.data || response.data || [];
          
          // Transform API notifications to our format
          const transformedNotifications = rawNotifications.map(n => {
            // Parse the data JSON string if needed
            let notifData = {};
            try {
              notifData = typeof n.data === 'string' ? JSON.parse(n.data) : (n.data || {});
            } catch (e) {
              notifData = {};
            }
            
            // Determine notification type based on API type
            let type = 'alert';
            let title = lang === 'ar' ? 'إشعار' : 'Notification';
            let message = '';
            
            if (n.type?.includes('AppointmentDone')) {
              type = 'success';
              title = lang === 'ar' ? 'اكتمال الموعد' : 'Appointment Completed';
              message = lang === 'ar' 
                ? `تم اكتمال موعدك رقم ${notifData.appointment_id}` 
                : `Your appointment #${notifData.appointment_id} is completed`;
            } else if (n.type?.includes('StatusChanged')) {
              type = 'appointment';
              title = lang === 'ar' ? 'تحديث الموعد' : 'Appointment Update';
              message = lang === 'ar' 
                ? `تم تحديث حالة موعدك رقم ${notifData.appointment_id}`
                : `Your appointment #${notifData.appointment_id} status has been updated`;
            } else if (n.type?.includes('NewAppointment')) {
              type = 'appointment';
              title = lang === 'ar' ? 'موعد جديد' : 'New Appointment';
              message = lang === 'ar' 
                ? 'لديك موعد جديد' 
                : 'You have a new appointment';
            } else if (n.type?.includes('Message')) {
              type = 'message';
              title = lang === 'ar' ? 'رسالة جديدة' : 'New Message';
              message = lang === 'ar' ? 'لديك رسالة جديدة' : 'You have a new message';
            }
            
            return {
              id: n.id,
              type,
              title,
              message,
              time: new Date(n.created_at),
              read: !!n.read_at,
            };
          });
          
          setNotifications(transformedNotifications);
        } else {
          // Show mock data for demo
          setNotifications(getMockNotifications(lang));
        }
      } catch (error) {
        console.error('Failed to fetch notifications:', error);
        setNotifications(getMockNotifications(lang));
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [isAuthenticated, lang]);

  const getIcon = (type) => {
    switch (type) {
      case 'appointment': return <Calendar className="h-5 w-5 text-teal-500" />;
      case 'message': return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case 'success': return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'alert': return <AlertCircle className="h-5 w-5 text-amber-500" />;
      default: return <Bell className="h-5 w-5 text-slate-500 dark:text-slate-400" />;
    }
  };

  const markAsRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  };

  const deleteNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="notifications-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Bell className="h-8 w-8" />
              <div>
                <h1 className="text-3xl md:text-4xl font-bold">
                  {lang === 'ar' ? 'الإشعارات' : 'Notifications'}
                </h1>
                {unreadCount > 0 && (
                  <p className="text-teal-100 mt-1">
                    {lang === 'ar' 
                      ? `لديك ${unreadCount} إشعارات غير مقروءة`
                      : `You have ${unreadCount} unread notifications`
                    }
                  </p>
                )}
              </div>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={markAllAsRead}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-4 w-4 mr-2" />
                {lang === 'ar' ? 'تحديد الكل كمقروء' : 'Mark all as read'}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-1/4" />
                      <Skeleton className="h-4 w-3/4" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-16">
            <Bell className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-700 mb-2">
              {lang === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400">
              {lang === 'ar' 
                ? 'ستظهر هنا إشعاراتك عندما تتلقى تحديثات'
                : 'Your notifications will appear here when you receive updates'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification) => (
              <Card 
                key={notification.id}
                className={`border-0 shadow-sm transition-all ${
                  !notification.read ? 'bg-teal-50/50 border-l-4 border-l-teal-500' : ''
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <div className="p-2 rounded-full bg-slate-100">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3 className={`font-medium ${!notification.read ? 'text-slate-900' : 'text-slate-700'}`}>
                            {notification.title}
                          </h3>
                          <p className="text-sm text-slate-600 mt-1">
                            {notification.message}
                          </p>
                        </div>
                        {!notification.read && (
                          <Badge className="bg-teal-500 shrink-0">
                            {lang === 'ar' ? 'جديد' : 'New'}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-1 text-xs text-slate-400">
                          <Clock className="h-3 w-3" />
                          <span>
                            {formatDistanceToNow(new Date(notification.time), { addSuffix: true, locale })}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {!notification.read && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => markAsRead(notification.id)}
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => deleteNotification(notification.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
